<template>
  <div id="video-player" v-if="currentVideo">
    <mq-layout mq="sm">
      <img @click="clearCurrentVideo()" id="video-back" class="pointer" src="../assets/arrow-left.svg" />
    </mq-layout>
    <div class="video-title center py2 px3 caps font-size-large align-right">
      <h3 class="m0 py1 px0" v-if="currentVideo">{{ currentVideo.title }}</h3>
    </div>
    <div class="soeTvLink" id="linkToSoeTv" @mouseover="enterSoeTvLink()" @mouseleave="leaveSoeTvLink()">
      <a v-bind:href="'/video/'+this.currentVideo.slug" target="_blank" title="Watch this video on SOE.TV">SOE.TV</a>
    </div>
    <div class="muteButton" id="buttonToMute">
      <button type="button" class="plyr__control plyr__control--pressed" data-plyr="mute" @click="unmuteVideo()">
        <svg class="icon--pressed" role="presentation" focusable="false">
          <use xlink:href="/static/plyr-soe.svg#plyr-muted"></use>
        </svg>
        <span class="label--pressed plyr__sr-only">Unmute</span>
      </button>
    </div>

     <video controls playsinline id="player" @click="scrollTop()"></video>

     <div id="scrolled-video-holder"></div>

    <div class="video-details py2 px2 sm-px3 clearfix relative">
      <div class="col col-12 sm-col-8 video-description">
        <p v-html="videoDescription" class="sm-pr3 font-size-large"></p>
        <ul class="caps" v-if="currentVideo.themes">
          <li class="theme-block mr1" v-for="theme in currentVideo.themes" :key="theme.id"><router-link :to='themeLink(theme)'>{{ theme.name }}</router-link></li>
        </ul>
        <span class="sm-hide">&nbsp;</span>
      </div>
      <div class="col col-12 sm-col-4 left-align sm-right-align">
        <p class="caps" v-if="currentVideo.channels">
          <span v-text="videoChannels"></span>
        </p>
        <p class="caps mb0">{{ getVideoTimeFromSeconds(currentVideo.duration, true) }}</p>
        <p class="mt0">{{ getFormattedVideoDate(currentVideo.created_at) }}</p>

        <ul class="caps">
          <li><router-link class="theme-block round mr0" :to="relatedLink">See Related Videos</router-link></li>
          <li v-if="this.$route.name !== 'Video'" @click="copyLink"><span class='theme-block round mr0 pointer' v-text="copyLinkText"></span></li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import Plyr from 'plyr'
import { getVideoTimeFromSeconds, getFormattedVideoDate } from '../utils'
import { mapActions, mapState } from 'vuex'

export default {

  name: 'VideoPlayer',

  data () {
    return {
      intersectionOptions: {
        root: null,
        rootMargin: '0px 0px 0px 0px',
        thresholds: [0]
      },
      player: null,
      videoLoaded: false,
      copyLinkText: 'Copy Link'
    }
  },

  watch: {
    $route (to, from) {
      if (to.name === 'Videos' || from.name === 'Videos') {
        if ((from.params.slug && `#${from.params.slug}` !== to.hash) || (to.params.slug && `#${to.params.slug}` !== from.hash)) {
          this.copyLinkText = 'Copy Link'
          this.playVideo()
        }
      } else if (to.hash !== from.hash) {
        this.copyLinkText = 'Copy Link'
        this.playVideo()
      }
    }
  },

  mounted: function () {
    this.playVideo()
  },

  computed: {
    ...mapState([
      'videos',
      'currentVideo'
    ]),
    relatedLink: function () {
      if (this.$route.hash) {
        return `/related/${this.currentVideo.slug}${this.$route.hash}`
      } else {
        return `/related/${this.currentVideo.slug}#${this.$route.params.slug}`
      }
    },
    videoChannels () {
      return Object.keys(this.currentVideo.channels).join(', ')
    },
    videoSrc: function () {
      let videoFiles = this.currentVideo.files.filter(file => file.quality === 'hd')

      if (videoFiles.length === 0) {
        videoFiles = this.currentVideo.files.filter(file => file.quality === 'sd')
      }

      return videoFiles[0].link_secure
    },
    videoPoster: function () {
      return this.currentVideo.image_small
    },
    videoDescription: function () {
      if (this.currentVideo.description) {
        return this.currentVideo.description
      } else {
        return '&nbsp;'
      }
    }
  },

  methods: {
    ...mapActions([
      'updateCurrentVideo',
      'getVideos',
      'clearVideo'
    ]),
    getVideoTimeFromSeconds,
    getFormattedVideoDate,
    copyLink () {
      let textArea = document.createElement('textarea')
      textArea.style.position = 'fixed'
      textArea.style.top = 0
      textArea.style.left = 0
      textArea.style.width = '2em'
      textArea.style.height = '2em'
      textArea.style.padding = 0
      textArea.style.border = 'none'
      textArea.style.outline = 'none'
      textArea.style.boxShadow = 'none'
      textArea.style.background = 'transparent'
      textArea.value = `https://soe.tv/video/${this.currentVideo.slug}`

      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()

      try {
        document.execCommand('copy')
        this.copyLinkText = 'Link Copied'
      } catch (err) {
        console.log('Oops, unable to copy')
      }
      document.body.removeChild(textArea)
    },
    clearCurrentVideo: function () {
      this.clearVideo()
      this.$router.push(this.$route.path)
    },
    playVideo: function () {
      if (this.player === null) {
        this.player = new Plyr('#player', {
          controls: ['play', 'progress', 'current-time', 'mute', 'volume', 'fullscreen'],
          iconUrl: '/static/plyr-soe.svg',
          fullscreen: { iosNative: true }
        })
        // about these '.on' events triggers, see 'for embeds' below
        this.player.on('controlsshown', this.controlsShown)
        this.player.on('controlshidden', this.controlsHidden)
        this.player.on('volumechange', this.volumeChanged)
        this.player.muted = true
        if (this.currentVideo.isEmbed) {
          this.player.on('canplay', () => {
            if (!this.player.hasAudio) {
              // loop embeded videos which don't have audio
              this.player.loop = true
            }
            if (this.player.hasAudio && this.player.muted) {
              // show the unmute button
              document.getElementById('buttonToMute').classList.add('show')
            }
          })
        }
      }
      this.updatePlayer()
    },
    updatePlayer () {
      this.player.source = {
        type: 'video',
        sources: [
          {
            src: this.videoSrc,
            type: 'video/mp4',
            size: 720
          }
        ]
      }
      document.getElementById('video-player').scrollTop = 0
      this.player.play()
    },
    scrollTop: function () {
    },
    themeLink: function (theme) {
      if (this.$route.hash) {
        return `/themes/${theme.slug}/${this.$route.hash}`
      } else {
        return `/themes/${theme.slug}#${this.$route.params.slug}`
      }
    },
    //
    // extra "soe.tv" link for embeds
    //
    controlsShown () {
      // when plyr shows the controls
      if (this.currentVideo.isEmbed) {
        this.controlsShown = true
        if (!this.currentVideo.notSoeVideo) {
          document.getElementById('linkToSoeTv').classList.add('show')
        }
      }
    },
    controlsHidden () {
      // when plyr hiddes the controls
      if (this.currentVideo.isEmbed) {
        this.controlsShown = false
        if (!this.currentVideo.keepSoeTvLink && !this.currentVideo.notSoeVideo) {
          document.getElementById('linkToSoeTv').classList.remove('show')
        }
      }
    },
    enterSoeTvLink () {
      // when the div is hovered
      if (this.currentVideo.isEmbed) {
        if (!this.currentVideo.notSoeVideo) {
          this.currentVideo.keepSoeTvLink = true
          setTimeout(() => this.player.toggleControls(true), 1)
        }
      }
    },
    leaveSoeTvLink () {
      // when mouse leaves div
      if (this.currentVideo.isEmbed) {
        if (!this.currentVideo.notSoeVideo) {
          this.currentVideo.keepSoeTvLink = false
          if (!this.controlsShown) {
            window.document.getElementById('linkToSoeTv').classList.remove('show')
          }
          setTimeout(() => this.player.toggleControls(false), 1)
        }
      }
    },
    //
    // extra mute control for embeds
    //
    volumeChanged () {
      // when plyr event volume changed is triggered
      if (this.currentVideo.isEmbed && this.player.hasAudio) {
        if (this.player.muted) {
          document.getElementById('buttonToMute').classList.add('show')
        } else {
          document.getElementById('buttonToMute').classList.remove('show')
        }
      }
    },
    unmuteVideo () {
      // when the embed volume button is clicked
      this.player.muted = false
      // this triggers the volumeChanged() above
    }
  }
}
</script>

<style lang="css">
  #video.single {
    #video-player {
      position: relative;
      left: 0;
      width: 100%;
    }

    .plyr {
      max-width: 960px;
      margin: 0 auto;
    }

    .video-details {
      max-width: 800px;
      margin: 0 auto;
    }
  }
  #video-player {
    position: fixed;
    left: calc(50vw + 1px);
    top: 0;
    width: calc(50vw - 1px);
    height: 100vh;
    overflow-y: scroll;
    background: var(--color-light);

    #video-back {
      width: 8px;
      position: absolute;
      top: 2.3em;
      left: 1.7em;
    }

    .video-title {
      letter-spacing: 1px;
    }

    img {
      width: 100%;
    }

    #scrolled-video-holder {
      display: none;
      width: 100vw;
      background: rgba(0,0,0,0.1);

      &.show {
        display: block;
      }
    }

    .plyr {
      .plyr__poster {
        background: var(--color-green) !important;
      }

      &.sticky {
        position: fixed;
        bottom: 2em;
        right: 2em;
        width: 11vw;
        cursor: pointer;
        z-index: 5000;

        .plyr__controls {
          display: none;
        }

        &:hover {
          width: 12.25vw;
        }
      }
    }
  }

  .video-description {
    .theme-block {
      margin-right: 0.5em;
    }

    a {
      color: var(--color-black);
      /*display: inline-block;*/
      border-bottom: 1px solid var(--color-green);
    }
  }

  .theme-block {
    color: var(--color-black);
    border: 1px solid var(--color-black);
    display: inline-block;
    padding: 0.25em 0.5em;
    margin: 0.25em 0;
    letter-spacing: 0;

    &.round {
      border-radius: 30px;
    }

    a {
      color: var(--color-black);
      border: none;
    }

    &:hover {
      background: var(--color-black);
      color: var(--color-green);
      a {
        color: var(--color-green);
      }
    }
  }

  #share-link {
    color: var(--color-black);
    position: fixed;
    top: 1em;
    right: 1em;
    padding: 0.25em;
    border: 1px solid var(--color-black);
  }

  .plyr__control--overlaid {
    background: transparent;
    box-shadow: none;
    svg {
      color: rgba(0,255,0,0.6);
      width: 64px;
      height: 64px;
    }

    &:hover {
      background: transparent;
      svg {
        color: var(--color-green);
      }
    }
  }

  .plyr--video {
    .plyr__controls {
      background: rgba(0,0,0,.2);
      padding-top: 12px;
    }

    .plyr__control {
      border-radius: 0;
    }

    .plyr__tooltip {
      font-size: 12px;
      font-family: 'IBM Plex Mono', monospace;
      border-radius: 0;
      background: rgba(0,255,0,0.3);
      color: var(--color-green);
      box-shadow: none;

      &:before {
        border-top-color: rgba(0,255,0,0.2);
      }
    }

    .plyr__menu__container {
      border-radius: 0;
      background: rgba(0,255,0,0.2);
      color: var(--color-green);

      &:after {
        border-top-color: rgba(0,255,0,0.2);
      }
    }

    .plyr__time, .plyr__time--current {
      font-size: 12px;
      font-family: 'IBM Plex Mono', monospace;
      color: var(--color-green);
    }

    .plyr__progress__buffer {
      box-shadow: none;
      /*background: rgba(0,255,0,0.3);*/
      display: none;
    }

    .plyr__menu__container label.plyr__control input[type=radio]:checked+span {
      background: var(--color-green);
    }
  }

  .plyr--full-ui input[type=range] {
    color: var(--color-green);

    &::-webkit-slider-thumb {
      background: var(--color-green);
      margin-top: -6px;
      border: none;
      box-shadow: none;
    }

    &::-webkit-slider-runnable-track {
      border-radius: 0;
      height: 1px;
    }
  }

  .plyr--video .plyr__controls .plyr__control.plyr__tab-focus, .plyr--video .plyr__controls .plyr__control, .plyr--video .plyr__controls .plyr__control[aria-expanded=true] {
    color: rgba(0,255,0,0.6);
    &:hover {
      color: var(--color-green);
      background: transparent;
    }

    &[aria-expanded=true] {
      color: var(--color-green);
      background: transparent;
    }
  }

  @media only screen and (max-width: 1024px) {
    #video-player {
      left: 0;
      width: 100vw;

      .theme-block {
        margin-left: 0;
        margin-right: 0.5em;
      }
    }
  }

  .soeTvLink {
    display: none;
    position: absolute;
    top: 0;
    right: 0;
    z-index: 10;
    width: 100vw;
  }

  .embed .soeTvLink {
    display: block;
    text-align: right;

    transition: all 0.25s ease-out;
    transform: translate(0, -100%);

    a {
      font-weight: 600;
      display: inline-block;
      padding: 20px;
      background-color: rgba(0,0,0,.2);
      color: rgba(14, 255, 0, 0.6);
      transition: all 0.25s ease-out;
      &:hover {
        color: rgba(14, 255, 0, 1);
      }
      @media only screen and (max-width: 815px) {
        color: rgba(14, 255, 0, 1);
      }
    }
  }

  .embed .soeTvLink.show {
    transform: translate(0, 0);
  }

  .muteButton {
    display: none;
  }

  .embed .muteButton {
    transform: scale(2);
    padding: 25px;
    position: absolute;
    top: 0;
    z-index: 20;
    color: var(--color-green);
    opacity: 0.6;
    transition: all 0.25s ease-out;
    cursor: pointer;
    &:hover {
      opacity: 1;
    }
  }

  .muteButton.show {
    display: block;
  }
</style>



// WEBPACK FOOTER //
// src/components/VideoPlayer.vue